import React, { Suspense } from "react";

import "../hassibot/translations";
import "./alternative-entrypoints/service-worker/client";
import "./custom-pwa-compat";
import { InteractionStatus } from "@azure/msal-browser";
import {
  AuthenticatedTemplate,
  MsalProvider,
  UnauthenticatedTemplate,
  useMsal,
} from "@azure/msal-react";
import { initAmplitude } from "@config/amplitude";
import appConfig from "@config/app";
import { initDatadogRum } from "@config/datadog";
import { initSentryWebApp } from "@config/sentry";
import * as Sentry from "@sentry/react";
import { UPGRADE_REQUIRED } from "@shared/libs/oh-errors/error-messages";
import { ohQueryClient } from "@shared/libs/react-query/query-client";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { detect, OperatingSystem } from "detect-browser";
import { browserHistory } from "hassibot/browser-history";
import { MaintenancePage } from "hassibot/component/maintenance-mode/MaintenancePage";
import { CurrentLegalEntityProvider } from "hassibot/current-legal-entity-provider";
import { useAuthSync } from "hassibot/services_v2/login/hooks/use-auth-sync";
import { useDeviceRegistration } from "hassibot/services_v2/login/hooks/use-device-registration";
import { msalInstance } from "hassibot/services_v2/login/msal";
import { useFollowServiceWorker } from "hassibot/services_v2/service-worker";
import "scss/main.scss";
import { mtLogger } from "shared-core";
import { HassibotError } from "shared-core/custom-error";

import {
  isChunkLoadError,
  recoverFromChunkLoadError,
  setupChunkLoadRecovery,
} from "./chunk-load-recovery";
import {
  APP_UPDATE_REQUIRED,
  BROADCAST_CHANNEL_APP_UPDATE,
  emitOnBroadcastChannel,
} from "./custom-broadcast-channel";
import { SessionContainer } from "./inside-components";
import {
  BadRequest,
  HoustonWeveGotAProblem,
  Loading,
  LoginStandaloneApp,
  OldChromeBrowser,
  OldSafariBrowser,
  The404,
  UnsupportedBrowser,
  WrongBrowserOnMobile,
} from "./outside-components";

mtLogger.log(`Welcome to Hassibot! I'm ${appConfig.BUILD_HASH}`);

if (import.meta.env.VITE_SENTRY_DSN) {
  initSentryWebApp();
}

setupChunkLoadRecovery();

const isProduction = appConfig.IS_DEV === false && appConfig.IS_TEST_ENVS === false;

if (isProduction) {
  if (appConfig.IS_DATADOG_RUM_ENABLED) {
    initDatadogRum();
  }
  initAmplitude(appConfig.DEVICE_ID);
}

// Start our shared worker early to give it a head start. Beware, as of
// 2023-11-16 this does not work an Android (see following refs). We
// fallback to "regular worker" there, with an abvious performance
// cost. Ref:
//   - https://caniuse.com/sharedworkers
//   - https://bugs.chromium.org/p/chromium/issues/detail?id=154571
window.SharedWorker &&
  new SharedWorker(new URL("./alternative-entrypoints/webworker", import.meta.url), {
    type: "module",
  });

const ShellContainer = React.lazy(() => import("./main"));
const GlobalStyle = React.lazy(() => import("../styles"));

const InsideContainer = () => {
  return (
    <SessionContainer>
      <CurrentLegalEntityProvider>
        <GlobalStyle />
        <ShellContainer />
      </CurrentLegalEntityProvider>
    </SessionContainer>
  );
};

// Outside the startup gate: push registration must not wait on the bootstrap.
const DeviceRegistrationEffect = () => {
  useDeviceRegistration();
  return null;
};

type ErrorBoundaryContainerState = {
  errorType?: number;
  eventId?: string;
  isChunkError?: boolean;
  isUpgradeRequired?: boolean;
};

/**
 * Error boundary for the authenticated app, ABOVE the startup <Suspense> gate so
 * an error page unmounts the suspended children (and their loading fallback).
 */
class ErrorBoundaryContainer extends React.PureComponent<
  React.PropsWithChildren<{}>,
  ErrorBoundaryContainerState
> {
  private unlistenHistory: (() => void) | undefined;

  // react-query re-throws the SAME error instance when children remount after a
  // navigation reset — capture each distinct error once, reuse its Sentry ref.
  private lastCapturedError: unknown = null;
  private lastCapturedErrorEventId: string | undefined;

  constructor(props) {
    super(props);
    this.state = { errorType: undefined };
  }

  componentDidMount() {
    // isUpgradeRequired is deliberately NOT reset: stay frozen on <Loading />
    // until the service worker wipes and reloads.
    this.unlistenHistory = browserHistory.listen(() => {
      if (this.state.errorType !== undefined) {
        this.setState({ errorType: undefined, eventId: undefined });
      }
    });
  }

  componentWillUnmount() {
    this.unlistenHistory?.();
  }

  componentDidCatch(error, errorInfo) {
    // Not a failure: signal the app-update listener (which wipes + reloads) and freeze.
    if (error?.message === UPGRADE_REQUIRED) {
      emitOnBroadcastChannel(BROADCAST_CHANNEL_APP_UPDATE, {
        type: APP_UPDATE_REQUIRED,
      });
      this.setState({ isUpgradeRequired: true });
      return;
    }

    const errorType = error.type || 520;
    const isChunkError = isChunkLoadError(error);

    // Do not trigger Sentry for a 400 & 404
    if ([400, 404].includes(errorType)) {
      this.setState({ errorType, isChunkError });
      return;
    }

    if (error === this.lastCapturedError) {
      this.setState({ errorType, eventId: this.lastCapturedErrorEventId, isChunkError });
      return;
    }

    let eventId: string | undefined;
    Sentry.withScope(scope => {
      Object.keys(errorInfo).forEach(key => {
        scope.setExtra(key, errorInfo[key]);
      });

      if (error instanceof HassibotError) {
        if (error.additionalData) {
          const additionalData = error.additionalData;
          Object.keys(additionalData).forEach(key => {
            scope.setExtra(key, additionalData[key]);
          });
        }
      }

      eventId = Sentry.captureException(error);
    });

    this.lastCapturedError = error;
    this.lastCapturedErrorEventId = eventId;
    this.setState({ errorType, eventId, isChunkError });
  }

  render() {
    const { errorType, eventId, isChunkError, isUpgradeRequired } = this.state;

    if (isUpgradeRequired) {
      return <Loading />;
    }

    if (errorType !== undefined) {
      return errorType === 400 ? (
        <BadRequest />
      ) : errorType === 404 ? (
        <The404 />
      ) : (
        <HoustonWeveGotAProblem
          eventId={eventId}
          onReload={isChunkError ? recoverFromChunkLoadError : undefined}
        />
      );
    }

    return this.props.children;
  }
}

/**
 * MSAL init is not suspendable, and during "startup" / "handleRedirect" both
 * templates render null — show <Loading /> instead of a blank screen. Any other
 * status renders children, so the login screen stays interactive.
 */
const MsalStartupGate = ({ children }: React.PropsWithChildren<{}>) => {
  const { inProgress } = useMsal();

  if (inProgress === InteractionStatus.Startup || inProgress === InteractionStatus.HandleRedirect) {
    return <Loading />;
  }

  return <>{children}</>;
};

/**
 * MsalStartupGate covers MSAL init, then a single <Suspense> covers the whole
 * authenticated bootstrap — one continuous full-screen <Loading />.
 */
const UserStateContainer = () => {
  useFollowServiceWorker();
  useAuthSync();

  return (
    <MsalProvider instance={msalInstance}>
      <MsalStartupGate>
        <UnauthenticatedTemplate>
          <LoginStandaloneApp />
        </UnauthenticatedTemplate>
        <AuthenticatedTemplate>
          <QueryClientProvider client={ohQueryClient}>
            <DeviceRegistrationEffect />
            <ErrorBoundaryContainer>
              <Suspense fallback={<Loading />}>
                <InsideContainer />
              </Suspense>
            </ErrorBoundaryContainer>
            <ReactQueryDevtools initialIsOpen={false} />
          </QueryClientProvider>
        </AuthenticatedTemplate>
      </MsalStartupGate>
    </MsalProvider>
  );
};

const biggerThan92DotX = (version: string) => {
  const [majorVersion, _minorVersion, _patchVersion] = version.split(".");
  if (!majorVersion) {
    throw new Error(`Invalid version: major=${majorVersion}`);
  }
  return parseInt(majorVersion) >= 92;
};

const biggerThan15Dot4 = (version: string) => {
  const [majorVersion, minorVersion] = version.split(".");
  if (!majorVersion || !minorVersion) {
    throw new Error(`Invalid version: major=${majorVersion} minor=${minorVersion}`);
  }
  return (
    parseInt(majorVersion) > 15 || (parseInt(majorVersion) === 15 && parseInt(minorVersion) >= 4)
  );
};

// Check if Hassibot can run in the current browser.
const TestBrowserContainer = () => {
  const browserInfos = detect();
  const browser =
    browserInfos && browserInfos.name && browserInfos.version && browserInfos.os
      ? {
          name: browserInfos.name,
          version: browserInfos.version,
          os: browserInfos.os as OperatingSystem,
        }
      : undefined;

  const browserIsChrome = browser ? browser.name === "chrome" : undefined;
  const isOldChromeBrowser = browser
    ? browserIsChrome && !biggerThan92DotX(browser.version)
    : undefined;

  const browserIsSafari = browser ? browser.name === "safari" || browser.name === "ios" : undefined;
  const isOldSafariBrowser = browser
    ? browserIsSafari && !biggerThan15Dot4(browser.version)
    : undefined;

  const isWrongBrowserOnMobile = browser
    ? (browser.os === "Android OS" && browser.name !== "chrome") ||
      (browser.os === "iOS" && !browserIsSafari)
    : undefined;
  if (browser && isWrongBrowserOnMobile) {
    return <WrongBrowserOnMobile infos={browserInfos} platform={browser.os} />;
  } else if (browser && isOldChromeBrowser) {
    return <OldChromeBrowser browserOs={browser.os} />;
  } else if (browser && isOldSafariBrowser) {
    return <OldSafariBrowser browserOs={browser.os} />;
  } else if (
    !window.WebSocket ||
    !navigator.serviceWorker ||
    !Object.fromEntries ||
    !window.BroadcastChannel
  ) {
    return <UnsupportedBrowser />;
  } else {
    return <UserStateContainer />;
  }
};

// Welcome to Hassibot!
export const Hassibot = () => {
  if (appConfig.IS_MAINTENANCE_MODE) {
    return <MaintenancePage />;
  }

  return <TestBrowserContainer />;
};
